/* ⚠️ GENERATED from app/services/profileDetails.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   🪪 profileDetails.ts — "Profile details": what a person says about themself
   on their profile, and who may read each line of it.

   Replaces the flat "About you" answers (services/profileFields — which pages
   still use) for PEOPLE. Shared by the phone, the browser and the server
   (twins generated by tools/gen-twins.mjs), so the three cannot disagree about
   what a detail is, who sees it, or how it reads.

   ── THE SECTIONS ────────────────────────────────────────────────────────────
     work        jobs — a company (searched, with its logo), a position, a city,
                 the years, "I currently work here"
     education   schools — high school / college / university, the school
                 (searched, with its logo), degree, field of study, the years
     places      current city and hometown (searched)
     basic       pronouns, languages spoken
     relationship
     bio         one short line under the name
     contact     website, and Facebook / Instagram / TikTok / X / YouTube / LinkedIn

   ── WHO SEES WHAT ───────────────────────────────────────────────────────────
   Every item carries its own audience: 'public' | 'friends' | 'only_me'.
   🟥 "FRIENDS" CANNOT BE A FIRESTORE RULE (a rule cannot ask "are these two
      friends?"), so the whole document lives where only its owner may read it
      (users/{uid}/private/details) and everybody else is handed THEIR cut by
      the server (profileDetailsRoutes.js → visibleTo). A line a viewer may not
      see never reaches their phone.
   ═══════════════════════════════════════════════════════════════════════════ */

export const AUDIENCES = ['public', 'friends', 'only_me'];
export const SCHOOL_KINDS = ['high_school', 'college', 'university', 'other'];
export const DEGREES = ['associate', 'diploma', 'bachelor', 'master', 'doctorate', 'certificate'];
export const RELATIONSHIPS = ['single', 'in_relationship', 'engaged', 'married', 'civil_union', 'complicated', 'separated', 'divorced', 'widowed'];
export const PRONOUNS = ['he_him', 'she_her', 'they_them'];
export const LINK_KEYS = ['facebook', 'instagram', 'tiktok', 'x', 'youtube', 'linkedin'];
export const LINK_BASE = {
  facebook: 'https://facebook.com/',
  instagram: 'https://instagram.com/',
  tiktok: 'https://tiktok.com/@',
  x: 'https://x.com/',
  youtube: 'https://youtube.com/@',
  linkedin: 'https://linkedin.com/in/'
};
/* ═══ LIMITS ═════════════════════════════════════════════════════════════ */
export const MAX_ITEMS = 10;
export const MAX_LANGS = 10;
export const MAX_TEXT = 100;
export const MAX_BIO = 160;
export const MIN_YEAR = 1940;
export const maxYear = now => new Date(now).getUTCFullYear() + 8;
export function emptyDetails() {
  return {
    v: 1,
    work: [],
    education: [],
    places: {},
    basic: {},
    contact: {
      links: {}
    }
  };
}

/* ═══ CLEANING — the server runs this on every save; the clients on load ═══ */
const str = (v, max = MAX_TEXT) => typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '';
const aud = (v, dflt = 'public') => AUDIENCES.indexOf(v) >= 0 ? v : dflt;
const oneOf = (v, list) => list.indexOf(v) >= 0 ? v : undefined;
const year = (v, now) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n >= MIN_YEAR && n <= maxYear(now) ? n : undefined;
};
const ID_RE = /^[A-Za-z0-9_-]{1,24}$/;
let _seq = 0;
export function newId(now = Date.now()) {
  _seq = (_seq + 1) % 1e6;
  return `${now.toString(36)}${_seq.toString(36)}`.slice(-24);
}

/**
 * 🟥 A LOGO IS ONLY EVER A PICTURE FROM A SOURCE WE CHOSE. A client could save
 *    any URL as "the logo" — and a picture from anywhere is a tracking pixel
 *    shown to everybody who opens the profile. Only the hosts the search itself
 *    returns are kept.
 */
export const LOGO_HOSTS = ['commons.wikimedia.org', 'upload.wikimedia.org', 'www.google.com', 'icons.duckduckgo.com'];
export function cleanLogo(v) {
  const s = typeof v === 'string' ? v.trim() : '';
  if (!s || s.length > 400) return undefined;
  const m = /^https:\/\/([^/?#]+)(\/[^\s]*)?$/i.exec(s);
  if (!m) return undefined;
  return LOGO_HOSTS.indexOf(m[1].toLowerCase()) >= 0 ? s : undefined;
}
export function cleanDomain(v) {
  const s = typeof v === 'string' ? v.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') : '';
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(s) && s.length <= 100 ? s : undefined;
}
export function cleanOrg(v) {
  if (!v || typeof v !== 'object') return undefined;
  const name = str(v.name);
  if (!name) return undefined;
  const out = {
    name
  };
  if (typeof v.qid === 'string' && /^Q\d{1,12}$/.test(v.qid)) out.qid = v.qid;
  const d = cleanDomain(v.domain);
  if (d) out.domain = d;
  const l = cleanLogo(v.logo);
  if (l) out.logo = l;
  return out;
}
export function cleanPlace(v) {
  if (!v || typeof v !== 'object') return undefined;
  const name = str(v.name);
  if (!name) return undefined;
  const out = {
    name
  };
  const r = str(v.region);
  if (r) out.region = r;
  const c = str(v.country);
  if (c) out.country = c;
  if (typeof v.cc === 'string' && /^[A-Za-z]{2}$/.test(v.cc)) out.cc = v.cc.toUpperCase();
  return out;
}
/** A link typed any of the ways people type one, kept as typed (≤ 200). */
const cleanLink = v => str(v, 200);
export function cleanWebsite(v) {
  const s = str(v, 200);
  if (!s) return '';
  const url = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  return /^https?:\/\/[^\s/?#]+\.[^\s/?#]+/i.test(url) ? url : '';
}
export function cleanDetails(raw, now = Date.now()) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const out = emptyDetails();
  const ids = new Set();
  const idOf = v => {
    let id = typeof v === 'string' && ID_RE.test(v) && !ids.has(v) ? v : newId(now);
    while (ids.has(id)) id = newId(now);
    ids.add(id);
    return id;
  };
  const span = x => {
    const current = x.current === true;
    let from = year(x.from, now);
    let to = current ? undefined : year(x.to, now);
    if (from && to && to < from) {
      const t = from;
      from = to;
      to = t;
    }
    return {
      from,
      to,
      current
    };
  };
  for (const w of (Array.isArray(r.work) ? r.work : []).slice(0, MAX_ITEMS)) {
    if (!w || typeof w !== 'object') continue;
    const org = cleanOrg(w.org);
    if (!org) continue;
    const item = {
      id: idOf(w.id),
      org,
      aud: aud(w.aud),
      ...span(w)
    };
    const title = str(w.title);
    if (title) item.title = title;
    const city = str(w.city);
    if (city) item.city = city;
    if (!item.current) delete item.current;
    if (!item.from) delete item.from;
    if (!item.to) delete item.to;
    out.work.push(item);
  }
  for (const e of (Array.isArray(r.education) ? r.education : []).slice(0, MAX_ITEMS)) {
    if (!e || typeof e !== 'object') continue;
    const org = cleanOrg(e.org);
    const degree = oneOf(e.degree, DEGREES);
    if (!org && !degree) continue; // a school or at least a degree
    const item = {
      id: idOf(e.id),
      kind: oneOf(e.kind, SCHOOL_KINDS) || (degree ? 'university' : 'other'),
      aud: aud(e.aud),
      ...span(e)
    };
    if (org) item.org = org;
    if (degree) item.degree = degree;
    const field = str(e.field);
    if (field) item.field = field;
    if (!item.current) delete item.current;
    if (!item.from) delete item.from;
    if (!item.to) delete item.to;
    out.education.push(item);
  }
  const pl = r.places && typeof r.places === 'object' ? r.places : {};
  for (const k of ['current', 'hometown']) {
    const p = cleanPlace(pl[k]);
    if (p) out.places[k] = {
      ...p,
      aud: aud(pl[k].aud)
    };
  }
  const b = r.basic && typeof r.basic === 'object' ? r.basic : {};
  const pr = b.pronouns && oneOf(b.pronouns.value, PRONOUNS);
  if (pr) out.basic.pronouns = {
    value: pr,
    aud: aud(b.pronouns.aud)
  };
  if (b.languages && Array.isArray(b.languages.value)) {
    const langs = Array.from(new Set(b.languages.value.map(x => str(x, 40)).filter(Boolean))).slice(0, MAX_LANGS);
    if (langs.length) out.basic.languages = {
      value: langs,
      aud: aud(b.languages.aud)
    };
  }
  const rel = r.relationship && oneOf(r.relationship.value, RELATIONSHIPS);
  if (rel) out.relationship = {
    value: rel,
    aud: aud(r.relationship.aud, 'friends')
  };
  const bio = r.bio && str(r.bio.value, MAX_BIO);
  if (bio) out.bio = {
    value: bio,
    aud: aud(r.bio.aud)
  };
  const c = r.contact && typeof r.contact === 'object' ? r.contact : {};
  const web = c.website && cleanWebsite(c.website.value);
  if (web) out.contact.website = {
    value: web,
    aud: aud(c.website.aud)
  };
  const links = c.links && typeof c.links === 'object' ? c.links : {};
  for (const k of LINK_KEYS) {
    const v = links[k] && cleanLink(links[k].value);
    if (v) out.contact.links[k] = {
      value: v,
      aud: aud(links[k].aud)
    };
  }
  return out;
}

/* ═══ WHO SEES WHAT ══════════════════════════════════════════════════════ */
export function canSee(a, rel) {
  if (rel === 'owner') return true;
  if (a === 'public') return true;
  return a === 'friends' && rel === 'friend';
}

/** The details as `rel` may see them — nothing they may not see is left in. */
export function visibleTo(d, rel) {
  const ok = x => !!x && canSee(x.aud, rel);
  const out = emptyDetails();
  out.work = d.work.filter(w => ok(w));
  out.education = d.education.filter(e => ok(e));
  if (ok(d.places.current)) out.places.current = d.places.current;
  if (ok(d.places.hometown)) out.places.hometown = d.places.hometown;
  if (ok(d.basic.pronouns)) out.basic.pronouns = d.basic.pronouns;
  if (ok(d.basic.languages)) out.basic.languages = d.basic.languages;
  if (ok(d.relationship)) out.relationship = d.relationship;
  if (ok(d.bio)) out.bio = d.bio;
  if (ok(d.contact.website)) out.contact.website = d.contact.website;
  for (const k of LINK_KEYS) {
    const v = d.contact.links[k];
    if (ok(v)) out.contact.links[k] = v;
  }
  /* 🟥 a stranger is not told what the owner keeps to friends: no audience
     is sent for an item they cannot see, and the ones they can see say
     only what they are */
  return out;
}
export function isEmpty(d) {
  if (!d) return true;
  return !d.work.length && !d.education.length && !d.places.current && !d.places.hometown && !d.basic.pronouns && !d.basic.languages && !d.relationship && !d.bio && !d.contact.website && !LINK_KEYS.some(k => !!d.contact.links[k]);
}

/* ═══ THE OLD "ABOUT YOU" ════════════════════════════════════════════════
   The flat answers (users/{uid}.profileFields public, profileVault/{uid}.fields
   hidden) become the new shape the first time anybody reads them: a public
   answer stays public, a hidden one becomes "Only me". Nothing is lost and
   nothing becomes MORE visible than it was. */
const LEGACY_DEGREE = {
  school: {
    kind: 'high_school'
  },
  college: {
    kind: 'college',
    degree: 'associate'
  },
  bachelor: {
    kind: 'university',
    degree: 'bachelor'
  },
  master: {
    kind: 'university',
    degree: 'master'
  },
  doctorate: {
    kind: 'university',
    degree: 'doctorate'
  }
};
export function fromLegacy(pub, hidden, now = Date.now()) {
  const P = pub && typeof pub === 'object' ? pub : {};
  const H = hidden && typeof hidden === 'object' ? hidden : {};
  const get = k => {
    const p = str(P[k], 200);
    if (p) return {
      v: p,
      a: 'public'
    };
    const h = str(H[k], 200);
    if (h) return {
      v: h,
      a: 'only_me'
    };
    return null;
  };
  const raw = {
    education: [],
    places: {},
    basic: {},
    contact: {
      links: {}
    }
  };
  const pr = get('pronouns');
  if (pr && PRONOUNS.indexOf(pr.v) >= 0) raw.basic.pronouns = {
    value: pr.v,
    aud: pr.a
  };
  const city = get('city');
  if (city) raw.places.current = {
    name: city.v,
    aud: city.a
  };
  const ed = get('education');
  if (ed && LEGACY_DEGREE[ed.v] && LEGACY_DEGREE[ed.v].degree) raw.education.push({
    ...LEGACY_DEGREE[ed.v],
    aud: ed.a
  });
  for (const k of LINK_KEYS) {
    const l = get(k);
    if (l) raw.contact.links[k] = {
      value: l.v,
      aud: l.a
    };
  }
  return cleanDetails(raw, now);
}

/* ═══ HOW IT READS ═══════════════════════════════════════════════════════ */
export function linkUrl(key, value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}\//i.test(raw)) return `https://${raw}`;
  const h = raw.replace(/^@+/, '').replace(/^\/+/, '');
  return h ? `${LINK_BASE[key]}${h}` : null;
}
export function linkHandle(value) {
  const v = String(value || '').trim();
  return v.replace(/^https?:\/\/(www\.)?[^/]+\/(in\/)?@?/i, '').replace(/^@+/, '').replace(/\/+$/, '') || v;
}
/** "2019 – 2023", "2021 – Present", "2018", "" */
export function periodLabel(x, lang) {
  const present = detailsL('present', lang);
  if (x.from && x.current) return `${x.from} – ${present}`;
  if (x.from && x.to) return x.from === x.to ? String(x.from) : `${x.from} – ${x.to}`;
  if (x.current) return present;
  if (x.to) return String(x.to);
  if (x.from) return String(x.from);
  return '';
}
/** The sentence a job or a school reads as on a profile. */
export function workLine(w, lang) {
  if (w.title) return fill(detailsL('title_at', lang), {
    title: w.title,
    org: w.org.name
  });
  return fill(detailsL(w.current || !w.to ? 'works_at' : 'worked_at', lang), {
    org: w.org.name
  });
}
export function eduLine(e, lang) {
  const org = e.org ? e.org.name : '';
  if (!org) return e.degree ? degreeLabel(e.degree, lang) : '';
  if (e.kind === 'high_school') return fill(detailsL(e.current ? 'goes_to' : 'went_to', lang), {
    org
  });
  return fill(detailsL(e.current ? 'studies_at' : 'studied_at', lang), {
    org
  });
}
export function eduSub(e, lang) {
  const bits = [];
  if (e.degree && e.org) bits.push(degreeLabel(e.degree, lang));
  if (e.field) bits.push(e.field);
  const p = periodLabel(e, lang);
  if (p) bits.push(p);
  return bits.join(' · ');
}
export function workSub(w, lang) {
  const bits = [];
  if (w.city) bits.push(w.city);
  const p = periodLabel(w, lang);
  if (p) bits.push(p);
  return bits.join(' · ');
}
export function placeLabel(p) {
  return [p.name, p.country].filter(Boolean).join(', ');
}
/** A country code as its flag. */
export function flagOf(cc) {
  if (!cc || !/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...cc.split('').map(ch => 0x1f1e6 + ch.charCodeAt(0) - 65));
}
/**
 * The few lines on the profile card, most telling first: the current job, the
 * latest school, where they live, where they are from, then the relationship.
 * The full list is "See all details".
 */
export function introLines(d, lang, max = 5) {
  const out = [];
  const jobs = d.work.slice().sort((a, b) => Number(!!b.current) - Number(!!a.current) || (b.from || 0) - (a.from || 0));
  if (jobs[0]) out.push({
    key: `w_${jobs[0].id}`,
    icon: 'briefcase',
    text: workLine(jobs[0], lang),
    logo: jobs[0].org.logo
  });
  const rank = {
    university: 3,
    college: 2,
    other: 1,
    high_school: 0
  };
  const schools = d.education.slice().sort((a, b) => Number(!!b.current) - Number(!!a.current) || rank[b.kind] - rank[a.kind] || (b.to || b.from || 0) - (a.to || a.from || 0));
  if (schools[0]) out.push({
    key: `e_${schools[0].id}`,
    icon: 'school',
    text: eduLine(schools[0], lang),
    logo: schools[0].org && schools[0].org.logo
  });
  if (d.places.current) out.push({
    key: 'pc',
    icon: 'home',
    text: fill(detailsL('lives_in', lang), {
      place: placeLabel(d.places.current)
    })
  });
  if (d.places.hometown) out.push({
    key: 'ph',
    icon: 'location',
    text: fill(detailsL('from_place', lang), {
      place: placeLabel(d.places.hometown)
    })
  });
  if (d.relationship) out.push({
    key: 'rel',
    icon: 'heart',
    text: relationshipLabel(d.relationship.value, lang)
  });
  return out.slice(0, max);
}
export function fill(tpl, vars) {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] != null ? vars[k] : '');
}

/* ═══ SEARCH RESULTS (the server builds them; the shape is shared) ═══════ */

/** A favicon picture for a web domain — the fallback logo. */
export function faviconFor(domain) {
  const d = cleanDomain(domain);
  return d ? `https://www.google.com/s2/favicons?domain=${d}&sz=128` : undefined;
}
/** A Wikimedia Commons file as a small PNG. */
export function commonsThumb(file, width = 96) {
  const f = typeof file === 'string' ? file.trim() : '';
  if (!f) return undefined;
  return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(f.replace(/ /g, '_'))}?width=${width}`;
}
const SCHOOL_WORDS = /(universit|college|school|institut|academ|polytechnic|seminary|conservator|faculty|lyc[eé]e|gymnasium|jami|جامعة|كلية|مدرسة|universidad|université|universität|universidade|университет|大学|学院|学校|विश्वविद्यालय)/i;
const COMPANY_WORDS = /(compan|corporat|business|enterprise|manufactur|brand|bank|airline|retail|conglomerate|startup|firm|agency|group|subsidiar|organi[sz]ation|publisher|studio|developer|operator|provider|chain|holding|maker|service|software|network|broadcaster|newspaper|hospital|ministry|government|club|foundation|شركة|empresa|entreprise|unternehmen|компания|会社|公司)/i;
/** Is this search hit the right kind of thing (by its Wikidata description)? */
export function hitKindOk(kind, desc) {
  const d = String(desc || '');
  if (!d) return kind === 'company';
  if (kind === 'school') return SCHOOL_WORDS.test(d);
  /* a company: described as one — and never a person (a life's years, a job title), a place or a work */
  if (/\(\d{3,4}\s*[–-]\s*\d{0,4}\)|\bborn\b/i.test(d)) return false;
  if (/\b(human|person|actor|actress|singer|footballer|politician|minister|writer|painter|village|city|town|river|mountain|film|album|song|novel|species|genus|family name|given name)\b/i.test(d)) return false;
  return COMPANY_WORDS.test(d) && !/universit|college|school/i.test(d);
}
/** Several sources' hits as one list: exact and prefix matches first, one per name. */
export function mergeHits(q, lists, max = 12) {
  const fold = s => String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').trim();
  const fq = fold(q);
  const seen = new Map();
  for (const list of lists) {
    for (const h of list || []) {
      if (!h || !h.name) continue;
      const k = fold(h.name);
      const had = seen.get(k);
      if (!had) {
        seen.set(k, {
          ...h
        });
        continue;
      }
      /* the same place from two sources: keep the richer one */
      if (!had.logo && h.logo) had.logo = h.logo;
      if (!had.domain && h.domain) had.domain = h.domain;
      if (!had.qid && h.qid) had.qid = h.qid;
      if (!had.desc && h.desc) had.desc = h.desc;
    }
  }
  const score = h => {
    const n = fold(h.name);
    return (n === fq ? 0 : n.startsWith(fq) ? 1 : n.indexOf(fq) >= 0 ? 2 : 3) * 10 + (h.logo ? 0 : 1);
  };
  return Array.from(seen.values()).sort((a, b) => score(a) - score(b)).slice(0, max);
}

/* ═══ LANGUAGES (the "Languages" picker) ═════════════════════════════════ */
export const LANGUAGES = ['Arabic', 'English', 'French', 'Spanish', 'German', 'Italian', 'Portuguese', 'Russian', 'Turkish', 'Persian', 'Urdu', 'Hindi', 'Bengali', 'Punjabi', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Malayalam', 'Kannada', 'Chinese (Mandarin)', 'Cantonese', 'Japanese', 'Korean', 'Vietnamese', 'Thai', 'Indonesian', 'Malay', 'Filipino', 'Burmese', 'Hebrew', 'Kurdish', 'Amharic', 'Somali', 'Swahili', 'Hausa', 'Yoruba', 'Igbo', 'Zulu', 'Afrikaans', 'Dutch', 'Swedish', 'Norwegian', 'Danish', 'Finnish', 'Icelandic', 'Polish', 'Czech', 'Slovak', 'Hungarian', 'Romanian', 'Bulgarian', 'Greek', 'Serbian', 'Croatian', 'Bosnian', 'Albanian', 'Ukrainian', 'Belarusian', 'Georgian', 'Armenian', 'Azerbaijani', 'Kazakh', 'Uzbek', 'Pashto', 'Dari', 'Nepali', 'Sinhala', 'Khmer', 'Lao', 'Mongolian', 'Tigrinya', 'Berber', 'Catalan', 'Basque', 'Irish', 'Welsh', 'Latin', 'Sign language'];

/* ═══ WORDS — ten languages, embedded (as profileFields does) ═════════════ */

const L = {
  title: {
    en: 'Profile details',
    ar: 'تفاصيل الملف الشخصي',
    es: 'Detalles del perfil',
    fr: 'Détails du profil',
    de: 'Profildetails',
    pt: 'Detalhes do perfil',
    ru: 'Сведения профиля',
    hi: 'प्रोफ़ाइल विवरण',
    ja: 'プロフィール詳細',
    zh: '个人资料详情'
  },
  edit: {
    en: 'Edit details',
    ar: 'تعديل التفاصيل',
    es: 'Editar detalles',
    fr: 'Modifier les détails',
    de: 'Details bearbeiten',
    pt: 'Editar detalhes',
    ru: 'Изменить сведения',
    hi: 'विवरण संपादित करें',
    ja: '詳細を編集',
    zh: '编辑详情'
  },
  add: {
    en: 'Add details',
    ar: 'إضافة تفاصيل',
    es: 'Añadir detalles',
    fr: 'Ajouter des détails',
    de: 'Details hinzufügen',
    pt: 'Adicionar detalhes',
    ru: 'Добавить сведения',
    hi: 'विवरण जोड़ें',
    ja: '詳細を追加',
    zh: '添加详情'
  },
  see_all: {
    en: 'See all details',
    ar: 'عرض كل التفاصيل',
    es: 'Ver todos los detalles',
    fr: 'Voir tous les détails',
    de: 'Alle Details ansehen',
    pt: 'Ver todos os detalhes',
    ru: 'Все сведения',
    hi: 'सभी विवरण देखें',
    ja: 'すべての詳細を見る',
    zh: '查看全部详情'
  },
  nothing: {
    en: 'Nothing to show yet',
    ar: 'لا شيء لعرضه بعد',
    es: 'Aún no hay nada',
    fr: 'Rien à afficher',
    de: 'Noch nichts',
    pt: 'Nada ainda',
    ru: 'Пока пусто',
    hi: 'अभी कुछ नहीं',
    ja: 'まだありません',
    zh: '暂无内容'
  },
  work: {
    en: 'Work',
    ar: 'العمل',
    es: 'Trabajo',
    fr: 'Emploi',
    de: 'Arbeit',
    pt: 'Trabalho',
    ru: 'Работа',
    hi: 'कार्य',
    ja: '職歴',
    zh: '工作'
  },
  education: {
    en: 'Education',
    ar: 'التعليم',
    es: 'Educación',
    fr: 'Études',
    de: 'Ausbildung',
    pt: 'Formação',
    ru: 'Образование',
    hi: 'शिक्षा',
    ja: '学歴',
    zh: '教育'
  },
  places: {
    en: 'Places lived',
    ar: 'الأماكن',
    es: 'Lugares',
    fr: 'Lieux',
    de: 'Wohnorte',
    pt: 'Lugares',
    ru: 'Места',
    hi: 'स्थान',
    ja: '居住地',
    zh: '居住地'
  },
  basic: {
    en: 'Basic info',
    ar: 'معلومات أساسية',
    es: 'Información básica',
    fr: 'Infos de base',
    de: 'Allgemeines',
    pt: 'Informações básicas',
    ru: 'Основное',
    hi: 'मूल जानकारी',
    ja: '基本情報',
    zh: '基本信息'
  },
  relationship: {
    en: 'Relationship',
    ar: 'الحالة الاجتماعية',
    es: 'Situación sentimental',
    fr: 'Situation amoureuse',
    de: 'Beziehungsstatus',
    pt: 'Relacionamento',
    ru: 'Семейное положение',
    hi: 'संबंध',
    ja: '交際ステータス',
    zh: '感情状况'
  },
  bio: {
    en: 'Bio',
    ar: 'نبذة',
    es: 'Biografía',
    fr: 'Bio',
    de: 'Bio',
    pt: 'Bio',
    ru: 'О себе',
    hi: 'परिचय',
    ja: '自己紹介',
    zh: '简介'
  },
  bio_ph: {
    en: 'Describe who you are',
    ar: 'صف نفسك',
    es: 'Describe quién eres',
    fr: 'Décrivez-vous',
    de: 'Beschreibe dich',
    pt: 'Descreva quem você é',
    ru: 'Расскажите о себе',
    hi: 'अपने बारे में बताएं',
    ja: '自分について書く',
    zh: '介绍一下自己'
  },
  contact: {
    en: 'Contact & links',
    ar: 'التواصل والروابط',
    es: 'Contacto y enlaces',
    fr: 'Contact et liens',
    de: 'Kontakt & Links',
    pt: 'Contato e links',
    ru: 'Контакты и ссылки',
    hi: 'संपर्क और लिंक',
    ja: '連絡先とリンク',
    zh: '联系方式和链接'
  },
  website: {
    en: 'Website',
    ar: 'الموقع الإلكتروني',
    es: 'Sitio web',
    fr: 'Site web',
    de: 'Website',
    pt: 'Site',
    ru: 'Сайт',
    hi: 'वेबसाइट',
    ja: 'ウェブサイト',
    zh: '网站'
  },
  pronouns: {
    en: 'Pronouns',
    ar: 'الضمائر',
    es: 'Pronombres',
    fr: 'Pronoms',
    de: 'Pronomen',
    pt: 'Pronomes',
    ru: 'Местоимения',
    hi: 'सर्वनाम',
    ja: '代名詞',
    zh: '人称代词'
  },
  languages: {
    en: 'Languages',
    ar: 'اللغات',
    es: 'Idiomas',
    fr: 'Langues',
    de: 'Sprachen',
    pt: 'Idiomas',
    ru: 'Языки',
    hi: 'भाषाएँ',
    ja: '言語',
    zh: '语言'
  },
  add_work: {
    en: 'Add a workplace',
    ar: 'إضافة مكان عمل',
    es: 'Añadir un trabajo',
    fr: 'Ajouter un emploi',
    de: 'Arbeitsplatz hinzufügen',
    pt: 'Adicionar trabalho',
    ru: 'Добавить место работы',
    hi: 'कार्यस्थल जोड़ें',
    ja: '職場を追加',
    zh: '添加工作单位'
  },
  add_school: {
    en: 'Add a school',
    ar: 'إضافة مدرسة أو جامعة',
    es: 'Añadir un centro',
    fr: 'Ajouter un établissement',
    de: 'Schule hinzufügen',
    pt: 'Adicionar escola',
    ru: 'Добавить учебное заведение',
    hi: 'स्कूल जोड़ें',
    ja: '学校を追加',
    zh: '添加学校'
  },
  add_language: {
    en: 'Add a language',
    ar: 'إضافة لغة',
    es: 'Añadir idioma',
    fr: 'Ajouter une langue',
    de: 'Sprache hinzufügen',
    pt: 'Adicionar idioma',
    ru: 'Добавить язык',
    hi: 'भाषा जोड़ें',
    ja: '言語を追加',
    zh: '添加语言'
  },
  company: {
    en: 'Company',
    ar: 'الشركة',
    es: 'Empresa',
    fr: 'Entreprise',
    de: 'Unternehmen',
    pt: 'Empresa',
    ru: 'Компания',
    hi: 'कंपनी',
    ja: '会社',
    zh: '公司'
  },
  position: {
    en: 'Position',
    ar: 'المسمى الوظيفي',
    es: 'Cargo',
    fr: 'Poste',
    de: 'Position',
    pt: 'Cargo',
    ru: 'Должность',
    hi: 'पद',
    ja: '役職',
    zh: '职位'
  },
  city_town: {
    en: 'City / town',
    ar: 'المدينة',
    es: 'Ciudad',
    fr: 'Ville',
    de: 'Ort',
    pt: 'Cidade',
    ru: 'Город',
    hi: 'शहर',
    ja: '市区町村',
    zh: '城市'
  },
  school: {
    en: 'School',
    ar: 'المدرسة / الجامعة',
    es: 'Centro educativo',
    fr: 'Établissement',
    de: 'Schule / Hochschule',
    pt: 'Instituição',
    ru: 'Учебное заведение',
    hi: 'संस्थान',
    ja: '学校',
    zh: '学校'
  },
  school_type: {
    en: 'Type',
    ar: 'النوع',
    es: 'Tipo',
    fr: 'Type',
    de: 'Art',
    pt: 'Tipo',
    ru: 'Тип',
    hi: 'प्रकार',
    ja: '種類',
    zh: '类型'
  },
  high_school: {
    en: 'High school',
    ar: 'ثانوية',
    es: 'Secundaria',
    fr: 'Lycée',
    de: 'Schule',
    pt: 'Ensino médio',
    ru: 'Школа',
    hi: 'हाई स्कूल',
    ja: '高校',
    zh: '高中'
  },
  college: {
    en: 'College',
    ar: 'كلية',
    es: 'Instituto',
    fr: 'Collège / IUT',
    de: 'Fachhochschule',
    pt: 'Faculdade',
    ru: 'Колледж',
    hi: 'कॉलेज',
    ja: '短大・専門',
    zh: '学院'
  },
  university: {
    en: 'University',
    ar: 'جامعة',
    es: 'Universidad',
    fr: 'Université',
    de: 'Universität',
    pt: 'Universidade',
    ru: 'Университет',
    hi: 'विश्वविद्यालय',
    ja: '大学',
    zh: '大学'
  },
  other_school: {
    en: 'Other',
    ar: 'أخرى',
    es: 'Otro',
    fr: 'Autre',
    de: 'Sonstiges',
    pt: 'Outro',
    ru: 'Другое',
    hi: 'अन्य',
    ja: 'その他',
    zh: '其他'
  },
  degree: {
    en: 'Degree',
    ar: 'الدرجة العلمية',
    es: 'Titulación',
    fr: 'Diplôme',
    de: 'Abschluss',
    pt: 'Grau',
    ru: 'Степень',
    hi: 'डिग्री',
    ja: '学位',
    zh: '学位'
  },
  field: {
    en: 'Field of study',
    ar: 'التخصص',
    es: 'Especialidad',
    fr: 'Domaine d’études',
    de: 'Fachrichtung',
    pt: 'Área de estudo',
    ru: 'Специальность',
    hi: 'अध्ययन क्षेत्र',
    ja: '専攻',
    zh: '专业'
  },
  associate: {
    en: 'Associate degree',
    ar: 'دبلوم مشارك',
    es: 'Técnico superior',
    fr: 'DUT / BTS',
    de: 'Associate Degree',
    pt: 'Tecnólogo',
    ru: 'Младший специалист',
    hi: 'एसोसिएट डिग्री',
    ja: '準学士',
    zh: '副学士'
  },
  diploma: {
    en: 'Diploma',
    ar: 'دبلوم',
    es: 'Diploma',
    fr: 'Diplôme',
    de: 'Diplom',
    pt: 'Diploma',
    ru: 'Диплом',
    hi: 'डिप्लोमा',
    ja: 'ディプロマ',
    zh: '文凭'
  },
  bachelor: {
    en: 'Bachelor’s degree',
    ar: 'بكالوريوس',
    es: 'Grado',
    fr: 'Licence',
    de: 'Bachelor',
    pt: 'Bacharelado',
    ru: 'Бакалавр',
    hi: 'स्नातक',
    ja: '学士',
    zh: '学士'
  },
  master: {
    en: 'Master’s degree',
    ar: 'ماجستير',
    es: 'Máster',
    fr: 'Master',
    de: 'Master',
    pt: 'Mestrado',
    ru: 'Магистр',
    hi: 'स्नातकोत्तर',
    ja: '修士',
    zh: '硕士'
  },
  doctorate: {
    en: 'Doctorate',
    ar: 'دكتوراه',
    es: 'Doctorado',
    fr: 'Doctorat',
    de: 'Promotion',
    pt: 'Doutorado',
    ru: 'Доктор наук / PhD',
    hi: 'पीएचडी',
    ja: '博士',
    zh: '博士'
  },
  certificate: {
    en: 'Certificate',
    ar: 'شهادة',
    es: 'Certificado',
    fr: 'Certificat',
    de: 'Zertifikat',
    pt: 'Certificado',
    ru: 'Сертификат',
    hi: 'प्रमाणपत्र',
    ja: '修了証',
    zh: '证书'
  },
  from_year: {
    en: 'From',
    ar: 'من',
    es: 'Desde',
    fr: 'De',
    de: 'Von',
    pt: 'De',
    ru: 'С',
    hi: 'से',
    ja: '開始',
    zh: '起'
  },
  to_year: {
    en: 'To',
    ar: 'إلى',
    es: 'Hasta',
    fr: 'À',
    de: 'Bis',
    pt: 'Até',
    ru: 'По',
    hi: 'तक',
    ja: '終了',
    zh: '止'
  },
  present: {
    en: 'Present',
    ar: 'حتى الآن',
    es: 'Actualidad',
    fr: 'Aujourd’hui',
    de: 'Heute',
    pt: 'Atual',
    ru: 'Настоящее время',
    hi: 'वर्तमान',
    ja: '現在',
    zh: '至今'
  },
  current_work: {
    en: 'I currently work here',
    ar: 'أعمل هنا حاليًا',
    es: 'Trabajo aquí actualmente',
    fr: 'J’y travaille actuellement',
    de: 'Ich arbeite derzeit hier',
    pt: 'Trabalho aqui atualmente',
    ru: 'Работаю здесь сейчас',
    hi: 'मैं अभी यहां काम करता/करती हूं',
    ja: '現在ここで働いています',
    zh: '目前在这里工作'
  },
  current_school: {
    en: 'I currently study here',
    ar: 'أدرس هنا حاليًا',
    es: 'Estudio aquí actualmente',
    fr: 'J’y étudie actuellement',
    de: 'Ich studiere derzeit hier',
    pt: 'Estudo aqui atualmente',
    ru: 'Учусь здесь сейчас',
    hi: 'मैं अभी यहां पढ़ता/पढ़ती हूं',
    ja: '現在ここで学んでいます',
    zh: '目前在这里学习'
  },
  current_city: {
    en: 'Current city',
    ar: 'المدينة الحالية',
    es: 'Ciudad actual',
    fr: 'Ville actuelle',
    de: 'Aktueller Wohnort',
    pt: 'Cidade atual',
    ru: 'Текущий город',
    hi: 'वर्तमान शहर',
    ja: '現在の居住地',
    zh: '现居城市'
  },
  hometown: {
    en: 'Hometown',
    ar: 'المدينة الأم',
    es: 'Ciudad natal',
    fr: 'Ville d’origine',
    de: 'Heimatstadt',
    pt: 'Cidade natal',
    ru: 'Родной город',
    hi: 'गृहनगर',
    ja: '出身地',
    zh: '家乡'
  },
  works_at: {
    en: 'Works at {org}',
    ar: 'يعمل في {org}',
    es: 'Trabaja en {org}',
    fr: 'Travaille chez {org}',
    de: 'Arbeitet bei {org}',
    pt: 'Trabalha na {org}',
    ru: 'Работает в {org}',
    hi: '{org} में काम करते हैं',
    ja: '{org}に勤務',
    zh: '就职于 {org}'
  },
  worked_at: {
    en: 'Worked at {org}',
    ar: 'عمل في {org}',
    es: 'Trabajó en {org}',
    fr: 'A travaillé chez {org}',
    de: 'Arbeitete bei {org}',
    pt: 'Trabalhou na {org}',
    ru: 'Работал(а) в {org}',
    hi: '{org} में काम किया',
    ja: '{org}に勤務していた',
    zh: '曾就职于 {org}'
  },
  title_at: {
    en: '{title} at {org}',
    ar: '{title} في {org}',
    es: '{title} en {org}',
    fr: '{title} chez {org}',
    de: '{title} bei {org}',
    pt: '{title} na {org}',
    ru: '{title} в {org}',
    hi: '{org} में {title}',
    ja: '{org}の{title}',
    zh: '{org} {title}'
  },
  studies_at: {
    en: 'Studies at {org}',
    ar: 'يدرس في {org}',
    es: 'Estudia en {org}',
    fr: 'Étudie à {org}',
    de: 'Studiert an der {org}',
    pt: 'Estuda na {org}',
    ru: 'Учится в {org}',
    hi: '{org} में पढ़ते हैं',
    ja: '{org}に在学中',
    zh: '就读于 {org}'
  },
  studied_at: {
    en: 'Studied at {org}',
    ar: 'درس في {org}',
    es: 'Estudió en {org}',
    fr: 'A étudié à {org}',
    de: 'Studierte an der {org}',
    pt: 'Estudou na {org}',
    ru: 'Учился(-ась) в {org}',
    hi: '{org} में पढ़ाई की',
    ja: '{org}で学んだ',
    zh: '曾就读于 {org}'
  },
  goes_to: {
    en: 'Goes to {org}',
    ar: 'يدرس في {org}',
    es: 'Va a {org}',
    fr: 'Va à {org}',
    de: 'Geht zur {org}',
    pt: 'Estuda na {org}',
    ru: 'Учится в {org}',
    hi: '{org} में पढ़ते हैं',
    ja: '{org}に通学中',
    zh: '就读于 {org}'
  },
  went_to: {
    en: 'Went to {org}',
    ar: 'درس في {org}',
    es: 'Fue a {org}',
    fr: 'Est allé à {org}',
    de: 'Ging zur {org}',
    pt: 'Estudou na {org}',
    ru: 'Учился(-ась) в {org}',
    hi: '{org} में पढ़े',
    ja: '{org}に通っていた',
    zh: '曾就读于 {org}'
  },
  lives_in: {
    en: 'Lives in {place}',
    ar: 'يعيش في {place}',
    es: 'Vive en {place}',
    fr: 'Habite à {place}',
    de: 'Wohnt in {place}',
    pt: 'Mora em {place}',
    ru: 'Живёт в {place}',
    hi: '{place} में रहते हैं',
    ja: '{place}在住',
    zh: '现居 {place}'
  },
  from_place: {
    en: 'From {place}',
    ar: 'من {place}',
    es: 'De {place}',
    fr: 'Originaire de {place}',
    de: 'Aus {place}',
    pt: 'De {place}',
    ru: 'Из {place}',
    hi: '{place} से',
    ja: '{place}出身',
    zh: '来自 {place}'
  },
  single: {
    en: 'Single',
    ar: 'أعزب',
    es: 'Soltero/a',
    fr: 'Célibataire',
    de: 'Single',
    pt: 'Solteiro(a)',
    ru: 'Не в отношениях',
    hi: 'अविवाहित',
    ja: '独身',
    zh: '单身'
  },
  in_relationship: {
    en: 'In a relationship',
    ar: 'في علاقة',
    es: 'En una relación',
    fr: 'En couple',
    de: 'In einer Beziehung',
    pt: 'Namorando',
    ru: 'В отношениях',
    hi: 'रिश्ते में',
    ja: '交際中',
    zh: '恋爱中'
  },
  engaged: {
    en: 'Engaged',
    ar: 'مخطوب',
    es: 'Comprometido/a',
    fr: 'Fiancé(e)',
    de: 'Verlobt',
    pt: 'Noivo(a)',
    ru: 'Помолвлен(а)',
    hi: 'सगाई हो चुकी',
    ja: '婚約中',
    zh: '已订婚'
  },
  married: {
    en: 'Married',
    ar: 'متزوج',
    es: 'Casado/a',
    fr: 'Marié(e)',
    de: 'Verheiratet',
    pt: 'Casado(a)',
    ru: 'В браке',
    hi: 'विवाहित',
    ja: '既婚',
    zh: '已婚'
  },
  civil_union: {
    en: 'In a civil union',
    ar: 'في اتحاد مدني',
    es: 'Pareja de hecho',
    fr: 'Pacsé(e)',
    de: 'Eingetragene Partnerschaft',
    pt: 'União estável',
    ru: 'Гражданский союз',
    hi: 'सिविल यूनियन में',
    ja: 'シビルユニオン',
    zh: '民事结合'
  },
  complicated: {
    en: 'It’s complicated',
    ar: 'الأمر معقد',
    es: 'Es complicado',
    fr: 'C’est compliqué',
    de: 'Es ist kompliziert',
    pt: 'É complicado',
    ru: 'Всё сложно',
    hi: 'जटिल है',
    ja: '複雑な関係',
    zh: '一言难尽'
  },
  separated: {
    en: 'Separated',
    ar: 'منفصل',
    es: 'Separado/a',
    fr: 'Séparé(e)',
    de: 'Getrennt',
    pt: 'Separado(a)',
    ru: 'Раздельно',
    hi: 'अलग',
    ja: '別居中',
    zh: '分居'
  },
  divorced: {
    en: 'Divorced',
    ar: 'مطلق',
    es: 'Divorciado/a',
    fr: 'Divorcé(e)',
    de: 'Geschieden',
    pt: 'Divorciado(a)',
    ru: 'В разводе',
    hi: 'तलाकशुदा',
    ja: '離婚',
    zh: '离异'
  },
  widowed: {
    en: 'Widowed',
    ar: 'أرمل',
    es: 'Viudo/a',
    fr: 'Veuf / veuve',
    de: 'Verwitwet',
    pt: 'Viúvo(a)',
    ru: 'Вдовец / вдова',
    hi: 'विधुर / विधवा',
    ja: '死別',
    zh: '丧偶'
  },
  he_him: {
    en: 'He / him',
    ar: 'هو',
    es: 'Él',
    fr: 'Il',
    de: 'Er',
    pt: 'Ele',
    ru: 'Он',
    hi: 'वह (पुरुष)',
    ja: '彼',
    zh: '他'
  },
  she_her: {
    en: 'She / her',
    ar: 'هي',
    es: 'Ella',
    fr: 'Elle',
    de: 'Sie',
    pt: 'Ela',
    ru: 'Она',
    hi: 'वह (महिला)',
    ja: '彼女',
    zh: '她'
  },
  they_them: {
    en: 'They / them',
    ar: 'هم',
    es: 'Elle',
    fr: 'Iel',
    de: 'They',
    pt: 'Elu',
    ru: 'Они',
    hi: 'वे',
    ja: 'その他',
    zh: 'TA'
  },
  public: {
    en: 'Public',
    ar: 'عام',
    es: 'Público',
    fr: 'Public',
    de: 'Öffentlich',
    pt: 'Público',
    ru: 'Все',
    hi: 'सार्वजनिक',
    ja: '公開',
    zh: '公开'
  },
  friends: {
    en: 'Friends',
    ar: 'الأصدقاء',
    es: 'Amigos',
    fr: 'Amis',
    de: 'Freunde',
    pt: 'Amigos',
    ru: 'Друзья',
    hi: 'मित्र',
    ja: '友達',
    zh: '好友'
  },
  only_me: {
    en: 'Only me',
    ar: 'أنا فقط',
    es: 'Solo yo',
    fr: 'Moi uniquement',
    de: 'Nur ich',
    pt: 'Só eu',
    ru: 'Только я',
    hi: 'केवल मैं',
    ja: '自分のみ',
    zh: '仅自己'
  },
  who_sees: {
    en: 'Who can see this?',
    ar: 'من يمكنه رؤية هذا؟',
    es: '¿Quién puede ver esto?',
    fr: 'Qui peut voir ceci ?',
    de: 'Wer kann das sehen?',
    pt: 'Quem pode ver isto?',
    ru: 'Кто это видит?',
    hi: 'इसे कौन देख सकता है?',
    ja: '公開範囲',
    zh: '谁可以看到？'
  },
  search_company: {
    en: 'Search companies',
    ar: 'ابحث عن شركة',
    es: 'Buscar empresas',
    fr: 'Rechercher une entreprise',
    de: 'Unternehmen suchen',
    pt: 'Pesquisar empresas',
    ru: 'Поиск компаний',
    hi: 'कंपनी खोजें',
    ja: '会社を検索',
    zh: '搜索公司'
  },
  search_school: {
    en: 'Search schools and universities',
    ar: 'ابحث عن مدرسة أو جامعة',
    es: 'Buscar centros educativos',
    fr: 'Rechercher un établissement',
    de: 'Schulen und Hochschulen suchen',
    pt: 'Pesquisar escolas e universidades',
    ru: 'Поиск учебных заведений',
    hi: 'स्कूल और विश्वविद्यालय खोजें',
    ja: '学校・大学を検索',
    zh: '搜索学校和大学'
  },
  search_city: {
    en: 'Search cities',
    ar: 'ابحث عن مدينة',
    es: 'Buscar ciudades',
    fr: 'Rechercher une ville',
    de: 'Orte suchen',
    pt: 'Pesquisar cidades',
    ru: 'Поиск городов',
    hi: 'शहर खोजें',
    ja: '都市を検索',
    zh: '搜索城市'
  },
  use_typed: {
    en: 'Use “{q}”',
    ar: 'استخدم «{q}»',
    es: 'Usar «{q}»',
    fr: 'Utiliser « {q} »',
    de: '„{q}“ verwenden',
    pt: 'Usar “{q}”',
    ru: 'Использовать «{q}»',
    hi: '“{q}” उपयोग करें',
    ja: '「{q}」を使う',
    zh: '使用“{q}”'
  },
  no_results: {
    en: 'No results',
    ar: 'لا نتائج',
    es: 'Sin resultados',
    fr: 'Aucun résultat',
    de: 'Keine Ergebnisse',
    pt: 'Sem resultados',
    ru: 'Ничего не найдено',
    hi: 'कोई परिणाम नहीं',
    ja: '結果なし',
    zh: '无结果'
  },
  save: {
    en: 'Save',
    ar: 'حفظ',
    es: 'Guardar',
    fr: 'Enregistrer',
    de: 'Speichern',
    pt: 'Salvar',
    ru: 'Сохранить',
    hi: 'सहेजें',
    ja: '保存',
    zh: '保存'
  },
  saved: {
    en: 'Saved',
    ar: 'تم الحفظ',
    es: 'Guardado',
    fr: 'Enregistré',
    de: 'Gespeichert',
    pt: 'Salvo',
    ru: 'Сохранено',
    hi: 'सहेजा गया',
    ja: '保存しました',
    zh: '已保存'
  },
  saving: {
    en: 'Saving…',
    ar: 'جارٍ الحفظ…',
    es: 'Guardando…',
    fr: 'Enregistrement…',
    de: 'Wird gespeichert…',
    pt: 'Salvando…',
    ru: 'Сохранение…',
    hi: 'सहेजा जा रहा है…',
    ja: '保存中…',
    zh: '保存中…'
  },
  done: {
    en: 'Done',
    ar: 'تم',
    es: 'Listo',
    fr: 'OK',
    de: 'Fertig',
    pt: 'Concluir',
    ru: 'Готово',
    hi: 'हो गया',
    ja: '完了',
    zh: '完成'
  },
  cancel: {
    en: 'Cancel',
    ar: 'إلغاء',
    es: 'Cancelar',
    fr: 'Annuler',
    de: 'Abbrechen',
    pt: 'Cancelar',
    ru: 'Отмена',
    hi: 'रद्द करें',
    ja: 'キャンセル',
    zh: '取消'
  },
  unsaved: {
    en: 'Save your changes?',
    ar: 'هل تريد حفظ التغييرات؟',
    es: '¿Guardar los cambios?',
    fr: 'Enregistrer les modifications ?',
    de: 'Änderungen speichern?',
    pt: 'Salvar as alterações?',
    ru: 'Сохранить изменения?',
    hi: 'बदलाव सहेजें?',
    ja: '変更を保存しますか？',
    zh: '保存更改吗？'
  },
  discard: {
    en: 'Discard',
    ar: 'تجاهل',
    es: 'Descartar',
    fr: 'Abandonner',
    de: 'Verwerfen',
    pt: 'Descartar',
    ru: 'Не сохранять',
    hi: 'छोड़ें',
    ja: '破棄',
    zh: '放弃'
  },
  remove: {
    en: 'Remove',
    ar: 'إزالة',
    es: 'Quitar',
    fr: 'Retirer',
    de: 'Entfernen',
    pt: 'Remover',
    ru: 'Удалить',
    hi: 'हटाएं',
    ja: '削除',
    zh: '删除'
  },
  save_failed: {
    en: 'That did not save. Please try again.',
    ar: 'لم يتم الحفظ. حاول مرة أخرى.',
    es: 'No se guardó. Inténtalo de nuevo.',
    fr: 'Échec de l’enregistrement. Réessayez.',
    de: 'Nicht gespeichert. Bitte erneut versuchen.',
    pt: 'Não foi salvo. Tente novamente.',
    ru: 'Не сохранилось. Попробуйте ещё раз.',
    hi: 'सहेजा नहीं गया। फिर कोशिश करें।',
    ja: '保存できませんでした。もう一度お試しください。',
    zh: '保存失败，请重试。'
  },
  optional: {
    en: 'Everything here is optional, and each line has its own audience.',
    ar: 'كل ما هنا اختياري، ولكل سطر جمهوره الخاص.',
    es: 'Todo es opcional y cada línea tiene su propio público.',
    fr: 'Tout est facultatif et chaque ligne a son propre public.',
    de: 'Alles ist optional, und jede Zeile hat ihr eigenes Publikum.',
    pt: 'Tudo é opcional e cada linha tem seu próprio público.',
    ru: 'Всё необязательно, и у каждой строки своя видимость.',
    hi: 'सब कुछ वैकल्पिक है, और हर पंक्ति की अपनी दृश्यता है।',
    ja: 'すべて任意で、項目ごとに公開範囲を選べます。',
    zh: '所有内容均为可选，每一项都可单独设置可见范围。'
  }
};
export function detailsL(key, lang) {
  const row = L[key];
  if (!row) return key;
  const l = String(lang || 'en').slice(0, 2);
  return row[l] || row.en || key;
}
export const degreeLabel = (d, lang) => detailsL(d, lang);
export const relationshipLabel = (r, lang) => detailsL(r, lang);
export const schoolKindLabel = (k, lang) => detailsL(k === 'other' ? 'other_school' : k, lang);
export const audienceIcon = {
  public: 'earth',
  friends: 'people',
  only_me: 'lock-closed'
};
